/**
 * Where somebody was going before they were sent to sign in.
 *
 * A member of staff opening an emailed /tickets/42 in a fresh browser went
 * /tickets/42 -> /contractor-login -> /login -> Microsoft -> /my-tickets, and
 * the ticket was forgotten. Only a scanned label (/a/:tag, pages/TagLanding)
 * was remembered. This is that mechanism, generalised: every guard that sends
 * somebody to the front door (App.js ToLogin) and the 401 interceptor
 * (lib/apiClient) remember the path first, and the shell takes them there
 * once the server says who they are.
 *
 * sessionStorage, not localStorage: it belongs to this tab and survives the
 * round trip to Microsoft, which is exactly the lifetime wanted. A private
 * window that refuses storage simply loses the courtesy.
 *
 * The key is the one TagLanding has always written, so a scanned label is
 * remembered the same way and nothing there had to change.
 */
export const RETURN_KEY = 'jarvis-after-sign-in';

// Never worth coming back to: the doors themselves, and the home that the
// router chooses anyway.
const DOORS = ['/login', '/contractor-login', '/'];

/** A path this app may safely be sent to: its own, absolute, never another host. */
export const isReturnable = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return false;
  const bare = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return !DOORS.includes(bare);
};

/** Remember `path` (pathname + search) for after sign-in. Doors are ignored. */
export function rememberReturnPath(path) {
  if (!isReturnable(path)) return;
  try { sessionStorage.setItem(RETURN_KEY, path); } catch { /* a private window */ }
}

/** The remembered path, taken (forgotten) as it is read; null when none. */
export function takeReturnPath() {
  let pending = null;
  try {
    pending = sessionStorage.getItem(RETURN_KEY);
    if (pending !== null) sessionStorage.removeItem(RETURN_KEY);
  } catch { /* a private window */ }
  return isReturnable(pending) ? pending : null;
}
